/** Inclusive list-filter date bounds. */
export type ListDateRangeBounds = {
  dateFrom?: Date;
  dateTo?: Date;
};

/** Input for {@link resolveListDateRange}: period key plus optional custom from/to. */
export type ResolveListDateRangeInput = {
  dateRange?: string;
  customDateFrom?: Date;
  customDateTo?: Date;
};

/** Options for {@link resolveListDateRange}: how to close a preset period (`now` vs last instant of the period). */
export type ResolveListDateRangeOptions = {
  now?: Date;
  periodEnd?: 'now' | 'endOfPeriod';
};

/** Local midnight at the start of `value`'s calendar day. */
function startOfLocalDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/** Local last millisecond of `value`'s calendar day. */
function endOfLocalDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999);
}

/** Local Monday 00:00 of the ISO-style week containing `value` (Mon–Sun). */
function startOfLocalWeekMonday(value: Date): Date {
  const from = startOfLocalDay(value);
  const daysFromMonday = (from.getDay() + 6) % 7;
  from.setDate(from.getDate() - daysFromMonday);
  return from;
}

/** Resolve a list date-range filter to `{ dateFrom, dateTo }`. */
export function resolveListDateRange(
  input: ResolveListDateRangeInput,
  options: ResolveListDateRangeOptions = {},
): ListDateRangeBounds {
  if (input.customDateFrom != null || input.customDateTo != null) {
    return { dateFrom: input.customDateFrom, dateTo: input.customDateTo };
  }

  const range = input.dateRange;
  if (!range || range === 'all') return {};

  const now = options.now ?? new Date();
  const periodEnd = options.periodEnd ?? 'now';

  if (range === 'today') {
    return { dateFrom: startOfLocalDay(now), dateTo: endOfLocalDay(now) };
  }

  if (range === 'week') {
    const from = startOfLocalWeekMonday(now);
    const to = new Date(from);
    to.setDate(to.getDate() + 6);
    return { dateFrom: from, dateTo: endOfLocalDay(to) };
  }

  if (range === 'month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    const to =
      periodEnd === 'endOfPeriod'
        ? new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
        : new Date(now);
    return { dateFrom: from, dateTo: to };
  }

  if (range === 'year') {
    const from = new Date(now.getFullYear(), 0, 1);
    const to =
      periodEnd === 'endOfPeriod'
        ? new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999)
        : new Date(now);
    return { dateFrom: from, dateTo: to };
  }

  return {};
}
